import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Toast, Loading } from 'react-vant'
import { APIWeb } from '@/api'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import Tabs from '@/components/Tabs'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import CardSwiper, { type CardData, type CardSwiperRef } from '../components/card-swiper'
import CardView from '../components/card-view'
import type { Item } from '@/components/Tabs/index.type'
import { CardFormFactor, parseCardMeta, type FeatureItem } from '../utils'

// 卡片类型枚举
export const enum CardType {
  Electronic = 'electronic',
  Physical = 'physical'
}

// 供应商卡片数据（扩展 CardData）
interface ProviderCard extends CardData {
  productId: number
  name: string
  type: CardType
  backgroundImage: string
  features: FeatureItem[]
  available: boolean
  bin?: string // 卡产品BIN（4位卡头）
  product?: ModelsCardProduct // 原始产品数据
}

export default function ApplyCard() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const swiperRef = useRef<CardSwiperRef>(null)

  // 从 URL 参数获取初始卡片类型
  const initialCardType = useMemo(() => {
    const typeParam = searchParams.get('type')
    if (typeParam === 'electronic') return CardType.Electronic
    return CardType.Physical
  }, [searchParams])

  const [loading, setLoading] = useState(true)
  const [productList, setProductList] = useState<ModelsCardProduct[]>([])
  const [selectedCardType, setSelectedCardType] = useState<CardType>(initialCardType)
  const [currentProviderIndex, setCurrentProviderIndex] = useState(0)

  // 获取产品列表
  const fetchProductList = useCallback(async () => {
    try {
      setLoading(true)
      const response = await APIWeb.getCardProductListV2()
      if (response?.list) {
        setProductList(response.list)
      }
    } catch (error) {
      console.error('获取产品列表失败:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // 页面加载时获取产品列表
  useEffect(() => {
    fetchProductList()
  }, [fetchProductList])

  // 根据产品数据转换为 ProviderCard
  const convertProductToCard = useCallback((product: ModelsCardProduct, index: number): ProviderCard => {
    const locale = i18n.language || 'zh-CN'
    const { name, features } = parseCardMeta(product.meta, locale)
    const isVirtual = product.form_factor === CardFormFactor.Virtual

    return {
      id: `product-${product.id}`,
      productId: product.id || 0,
      name: name || product.name || `卡片 ${index + 1}`,
      type: isVirtual ? CardType.Electronic : CardType.Physical,
      cardType: isVirtual ? 'virtual' : 'physical',
      backgroundImage: (product as any).image || '/images/card-v2/electronic-card/zise-card-1.png',
      features,
      available: product.status === 'available',
      bin: product.bin,
      product
    }
  }, [i18n.language])

  // 电子卡列表
  const electronicProviders = useMemo(() => {
    return productList
      .filter(p => p.form_factor === CardFormFactor.Virtual)
      .map((p, index) => convertProductToCard(p, index))
  }, [productList, convertProductToCard])

  // 实体卡列表
  const physicalProviders = useMemo(() => {
    return productList
      .filter(p => p.form_factor === CardFormFactor.Physical)
      .map((p, index) => convertProductToCard(p, index))
  }, [productList, convertProductToCard])

  // 当前供应商列表
  const currentProviders = selectedCardType === CardType.Electronic
    ? electronicProviders
    : physicalProviders

  // 当前选中的卡片
  const currentProvider = useMemo(() => {
    return currentProviders[currentProviderIndex] || null
  }, [currentProviders, currentProviderIndex])

  // Tab 数据
  const tabsItems = useMemo<Item<CardType>[]>(() => [
    { type: CardType.Physical, text: t('实体卡') },
    { type: CardType.Electronic, text: t('电子卡') }
  ], [t])

  // 处理 Tab 切换（同步更新 URL 的 type 参数）
  const handleTabChange = useCallback((cardType: CardType) => {
    setSelectedCardType(cardType)
    setCurrentProviderIndex(0)
    swiperRef.current?.slideTo(0)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('type', cardType)
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  // 处理卡片切换
  const handleCardChange = useCallback((_card: CardData, index: number) => {
    setCurrentProviderIndex(index)
  }, [])

  // 处理立即申请
  const handleApply = useCallback(() => {
    if (!currentProvider) return

    // 卡片不可用时提示即将上线
    if (!currentProvider.available) {
      Toast.info(t('即将上线'))
      return
    }
    // 跳转到卡片详情页，传递产品数据
    navigate(`/card-v2/card-detail?type=${selectedCardType}&productId=${currentProvider.productId}`, {
      state: { product: currentProvider.product }
    })
  }, [navigate, selectedCardType, currentProvider, t])

  // 处理绑定已有卡（先选择持卡人，再输入卡号和CVV）
  const handleBindExistingCard = useCallback(() => {
    if (!currentProvider) return

    if (!currentProvider.available) {
      Toast.info(t('即将上线'))
      return
    }

    // 准备绑定参数
    const bindData = {
      productId: currentProvider.productId,
      product: currentProvider.product,
      backgroundImage: currentProvider.backgroundImage,
      cardBin: currentProvider.bin,
    }

    // 跳转到选择持卡人页面，传入产品ID用于筛选符合开卡的持卡人，携带 action=bind 标识绑定流程
    navigate(`/card-v2/select-cardholder?type=physical&action=bind${currentProvider.productId ? `&productId=${currentProvider.productId}` : ''}`, {
      state: { bindData }
    })
  }, [navigate, currentProvider, t])

  // 自定义卡片渲染
  const renderCard = useCallback((card: CardData) => {
    const provider = card as ProviderCard
    return (
      <CardView
        cardStatus="pending"
        backgroundImage={provider.backgroundImage}
        cardBin={provider.bin}
      />
    )
  }, [])

  // 判断当前卡片是否可申请
  const canApply = currentProvider && currentProvider.available

  // 实体卡才显示绑定已有卡按钮
  const showBindButton = canApply && selectedCardType === CardType.Physical

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('申请卡片')}</span>}
      />

      <div className="px-5 pt-4">
        {/* 卡片类型切换 */}
        <Tabs<CardType>
          items={tabsItems}
          defaultSelected={selectedCardType}
          onChange={handleTabChange}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loading type="ball" />
        </div>
      ) : (
        <>
          {/* 供应商卡片 Swiper */}
          <div className="mt-6">
            <CardSwiper
              ref={swiperRef}
              cards={currentProviders}
              onCardChange={handleCardChange}
              renderCard={renderCard}
            />
          </div>

          {/* 供应商名称 */}
          <div className="px-5">
            <h2 className="text-xl font-bold text-[#1D2129] text-center my-6">
              {currentProvider?.name || ''}
            </h2>

            {/* 特性列表 */}
            <div className="space-y-4 pb-34">
              {currentProvider?.features.map((feature: FeatureItem, index: number) => (
                <div key={index} className="flex items-center gap-3">
                  <div className='flex items-center justify-center w-8 h-8 bg-[#F4F6F9] rounded-full flex-shrink-0'>
                    <img src={feature.icon} alt="" />
                  </div>
                  <span className="text-sm text-[#4E5969] leading-6">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 固定在底部的申请按钮 */}
      <FixedBottom className='bg-white'>

        <div className={showBindButton ? 'flex justify-between gap-4' : ''}>
          {showBindButton && (
            <div className='flex-1'>
              <CustomButton
                color='default'
                onClick={handleBindExistingCard}
              >
                {t('绑定已有卡')}
              </CustomButton>
            </div>
          )}
          <div className={showBindButton ? 'flex-1' : ''}>
            <CustomButton
              onClick={handleApply}
              disabled={!canApply}
            >
              {t('立即申请') }
            </CustomButton>
          </div>
        </div>
        {currentProvider?.available && (
          <div
            className="text-sm text-[var(--primary-color)] leading-6 text-center mt-4 underline cursor-pointer"
            onClick={() => navigate(`/card-v2/redemption-code?type=${selectedCardType}&productId=${currentProvider.productId}`, {
              state: { product: currentProvider.product }
            })}
          >
            {t('卡片兑换码')}
          </div>
        )}
      </FixedBottom>
    </PageLayout>
  )
}
